import { saveAs } from 'file-saver';
import { formattedUserFacingMonthDayYearLong } from 'DateHelpers';
import { type FrontRoyalWindow } from 'FrontRoyalWindow';
import { getFilePathsFromManifest } from 'AssetLoader';
import loadjs from 'loadjs';
import proximaNovaSoftRegularAssetPath from '@fonts/ProximaNovaSoft-Regular.ttf';
import proximaNovaSoftSemiboldAssetPath from '@fonts/ProximaNovaSoft-Semibold.ttf';
import contentPageTemplatePath from 'raw/project_feedback/ProjectFeedbackContentPage.png';
import { type LearnerProject } from 'LearnerProjects';
import { type ProjectProgress } from 'ContentProgress';
import { type Brand, fetchBrandConfig } from 'AppBranding';
import { type TFunction } from 'i18next';
import { type ErrorLogService as ErrorLogServiceClass } from 'ErrorLogging';
import { angularInjectorProvider } from 'Injector';
import { blobToDataURL, downloadSupported, shareSupported } from 'ShareableGraphics';
// parseMarkdownForPdf uses remark-parse to convert markdown into a flat ContentBlock[]
// structure (headings, paragraphs, lists with TextSegments). This file handles only
// PDF rendering — layout, fonts, page breaks, PDFKit calls — without needing to
// understand the mdast AST directly. See parseMarkdownForPdf.ts for the parsing layer.
import {
    type ContentBlock,
    type ListItem,
    type TableRow,
    type TextSegment,
    parseMarkdownForPdf,
} from './parseMarkdownForPdf';

const MAX_PROJECT_SCORE = 5;
const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const CONTENT_LEFT = 72;
const CONTENT_WIDTH = 468; // 612 - 72 - 72
const FOOTER_Y = 740; // just below the template footer line
const PAGE_BREAK_Y = 720; // stop rendering content above the template footer line

// Page 1 Y positions (below the logo)
const PAGE_1_TITLE_Y = 130;
const PAGE_1_SUBTITLE_Y_OFFSET = 10; // below the title
const PAGE_1_METADATA_Y_OFFSET = 30; // below the subtitle

// Page 2+ header
const PAGE_N_HEADER_Y = 30;
const PAGE_N_CONTENT_START_Y = 65;

// Font sizes
const TITLE_FONT_SIZE = 33;
const SUBTITLE_FONT_SIZE = 20;
const METADATA_FONT_SIZE = 14;
const BODY_FONT_SIZE = 12;
const HEADING_FONT_SIZE = 14;
const RUNNING_HEADER_FONT_SIZE = 11;
const COPYRIGHT_FONT_SIZE = 11;
const EXPLANATION_FONT_SIZE = 12;
const CONTENT_LINE_GAP = 2;

// Spacing
const SPACE_BEFORE_HEADING = 10;
const SPACE_AFTER_HEADING = 8;
const PARAGRAPH_GAP = 6;
const LIST_ITEM_INDENT = 15;
const LIST_ITEM_GAP = 3;
const MIN_CONTENT_AFTER_HEADING = 45; // ≈3 lines of body text, prevents orphaned headings
const MIN_PARAGRAPH_HEIGHT = 2 * (BODY_FONT_SIZE + CONTENT_LINE_GAP);

// Code block styling
const CODE_BLOCK_FONT_SIZE = 11;
const CODE_BLOCK_PADDING = 8;
const CODE_BLOCK_LINE_GAP = 2;
const CODE_BLOCK_BORDER_RADIUS = 3;
const CODE_BLOCK_MARGIN_TOP = 8;
const CODE_BLOCK_MARGIN_BOTTOM = 14;

// Table styling (matches Tailwind: border-gray-300, bg-gray-50, px-3 py-2)
const TABLE_CELL_PADDING_X = 8;
const TABLE_CELL_PADDING_Y = 5;
const TABLE_BORDER_COLOR = '#D1D5DB'; // Tailwind gray-300
const TABLE_HEADER_BG = '#F9FAFB'; // Tailwind gray-50
const TABLE_BORDER_WIDTH = 0.5;
const TABLE_MARGIN_TOP = 8;
const TABLE_MARGIN_BOTTOM = 8;

// Horizontal rule styling (matches Tailwind: my-6 border-gray-300)
const HR_MARGIN_Y = 12;
const HR_COLOR = '#D1D5DB'; // Tailwind gray-300

// Blockquote styling (matches Tailwind: my-4 border-l-4 border-gray-300 pl-4 italic)
const BLOCKQUOTE_BORDER_WIDTH = 4;
const BLOCKQUOTE_BORDER_COLOR = '#D1D5DB'; // Tailwind gray-300
const BLOCKQUOTE_PADDING_LEFT = 12;
const BLOCKQUOTE_MARGIN_Y = 8;

// Inline code styling (pill background for `code` in running text)
const CODE_INLINE_PADDING_X = 3;
const CODE_INLINE_PADDING_Y = 1;
const CODE_INLINE_RADIUS = 2;
const CODE_INLINE_SPACING = 2; // horizontal space before/after inline code tokens

// Colors
const COLOR_BLACK = '#000000';
const COLOR_MUTED = '#708090'; // slate grey
const COLOR_RED = '#E53E3E';
const COLOR_EXPLANATION = '#708090';
const COLOR_CODE = '#c7254e'; // Bootstrap 3 $code-color, used for both inline code and code blocks
const LINK_COLOR = '#2563EB';
const CODE_BG_COLOR = '#F3F4F6'; // Tailwind gray-100

type FetchedAssets = {
    pages: { page1: ArrayBuffer; page2: ArrayBuffer };
    fonts: { regular: ArrayBuffer; semibold: ArrayBuffer };
};

type ExplanationSegment = { text: string; bold?: boolean; link?: string };

// oxlint-disable-next-line quantic/no-type-exports
export type DownloadProjectFeedbackPdfParams = {
    project: LearnerProject;
    progress: ProjectProgress;
    passed: boolean;
    failed: boolean;
    brand: Brand;
    t: TFunction;
    groupMemberNames?: string[];
    submitterName?: string;
};

async function loadPdfDependencies(): Promise<void> {
    // PDFDocument and blobStream are only available after loading the certificates bundle.
    // They are typed as non-optional on FrontRoyalWindow, but aren't present until loaded.
    if ('PDFDocument' in window && 'blobStream' in window) return;

    const certificatesPaths = getFilePathsFromManifest('certificates', 'js');
    if (certificatesPaths.length > 0) {
        await loadjs(
            certificatesPaths.map(p => `/${p}`),
            { returnPromise: true, numRetries: 3 },
        );
    }
}

async function fetchAssets(brand: Brand): Promise<FetchedAssets> {
    const brandConfig = fetchBrandConfig(brand);
    const coverPagePath = brandConfig.downloadableProjectFeedbackConfig.coverPage;
    if (!coverPagePath) {
        throw new Error(`Project feedback PDF is not available for brand: ${brand}`);
    }
    const [page1Response, page2Response, regularFontResponse, semiboldFontResponse] = await Promise.all([
        fetch(window.ENDPOINT_ROOT + coverPagePath),
        fetch(window.ENDPOINT_ROOT + contentPageTemplatePath),
        fetch(window.ENDPOINT_ROOT + proximaNovaSoftRegularAssetPath),
        fetch(window.ENDPOINT_ROOT + proximaNovaSoftSemiboldAssetPath),
    ]);

    const [page1, page2, regular, semibold] = await Promise.all([
        page1Response.blob().then(b => b.arrayBuffer()),
        page2Response.blob().then(b => b.arrayBuffer()),
        regularFontResponse.blob().then(b => b.arrayBuffer()),
        semiboldFontResponse.blob().then(b => b.arrayBuffer()),
    ]);

    return {
        pages: { page1, page2 },
        fonts: { regular, semibold },
    };
}

function getCopyrightMessage(): string {
    return `\u00A9 ${new Date().getFullYear()} Quantic Holdings, Inc. All rights reserved.`;
}

function addCopyrightFooter(doc: PDFKit.PDFDocument, fonts: FetchedAssets['fonts']): void {
    // Temporarily disable auto-pagination so the footer text at FOOTER_Y (740),
    // which is below PAGE_BREAK_Y (700), doesn't trigger an addPage() call.
    // Without this, calling addCopyrightFooter from the pageAdded handler would
    // cause infinite recursion: footer text → auto-paginate → pageAdded → footer text → ...
    const savedBottomMargin = doc.page.margins.bottom;
    doc.page.margins.bottom = 0;
    doc.font(fonts.regular)
        .fontSize(COPYRIGHT_FONT_SIZE)
        .fillColor(COLOR_MUTED)
        .text(getCopyrightMessage(), CONTENT_LEFT, FOOTER_Y);
    doc.page.margins.bottom = savedBottomMargin;
}

type I18nComponent = { text?: string; link?: string; bold?: boolean; visible?: boolean };
type TagMatch = { index: number; fullLength: number; tagName: string; selfClosing: boolean; innerContent?: string };

// Find the earliest <tag/> or <tag>...</tag> in the string
function findEarliestTag(text: string): TagMatch | null {
    const selfClosingMatch = /<(\w+)\s*\/>/.exec(text);
    const wrappingMatch = /<(\w+)>([\s\S]*?)<\/\1>/.exec(text);

    let earliest: TagMatch | null = null;
    if (selfClosingMatch?.index !== undefined) {
        earliest = {
            index: selfClosingMatch.index,
            fullLength: selfClosingMatch[0].length,
            tagName: selfClosingMatch[1],
            selfClosing: true,
        };
    }
    if (wrappingMatch?.index !== undefined && (!earliest || wrappingMatch.index < earliest.index)) {
        earliest = {
            index: wrappingMatch.index,
            fullLength: wrappingMatch[0].length,
            tagName: wrappingMatch[1],
            selfClosing: false,
            innerContent: wrappingMatch[2],
        };
    }
    return earliest;
}

// Parse an i18n template string containing <tag/> and <tag>...</tag> markers into segments.
// Self-closing tags (e.g. <projectGradingLink/>) are replaced with the component's text/link.
// Wrapping tags (e.g. <strong>...</strong>) apply formatting to their content.
// Wrapping tags with visible=false (e.g. <resubmitByDate>) are stripped entirely.
function parseI18nTemplate(template: string, components: Record<string, I18nComponent>): ExplanationSegment[] {
    const segments: ExplanationSegment[] = [];
    let remaining = template;

    while (remaining.length > 0) {
        const earliest = findEarliestTag(remaining);
        if (!earliest) {
            if (remaining) segments.push({ text: remaining });
            break;
        }

        if (earliest.index > 0) {
            segments.push({ text: remaining.substring(0, earliest.index) });
        }

        const component = components[earliest.tagName];
        if (earliest.selfClosing) {
            if (component?.text) {
                segments.push({ text: component.text, link: component.link, bold: component.bold });
            }
        } else if (component?.visible !== false && earliest.innerContent) {
            const innerSegments = parseI18nTemplate(earliest.innerContent, components);
            innerSegments.forEach(s => {
                if (component?.bold) s.bold = true;
            });
            segments.push(...innerSegments);
        }

        remaining = remaining.substring(earliest.index + earliest.fullLength);
    }

    return segments;
}

function getPassedExplanationSegments(t: TFunction, score: number): ExplanationSegment[] {
    const origin = window.location.origin;
    const template = t('project_grade_explanation_passed', { score, maxScore: MAX_PROJECT_SCORE });
    return parseI18nTemplate(template, {
        projectGradingLink: { text: t('graded'), link: `${origin}/help/project-grading` },
        codeOfConductLink: { text: t('code_of_conduct'), link: `${origin}/help/student-code-of-conduct` },
    });
}

function getResubmissionRequiredExplanationSegments(t: TFunction, progress: ProjectProgress): ExplanationSegment[] {
    const showResubmission = !!progress.resubmissionDueDate;
    const dueDate = progress.resubmissionDueDate
        ? formattedUserFacingMonthDayYearLong(progress.resubmissionDueDate * 1000)
        : '';
    const template = t('project_grade_explanation_resubmission_required', { maxScore: MAX_PROJECT_SCORE, dueDate });
    return parseI18nTemplate(template, {
        resubmitByDate: { visible: showResubmission },
        strong: { bold: true },
    });
}

function getFailedExplanationSegments(t: TFunction, score: number, progress: ProjectProgress): ExplanationSegment[] {
    const showResubmission = !!(progress.allowResubmission && progress.resubmissionDueDate);
    const dueDate = progress.resubmissionDueDate
        ? formattedUserFacingMonthDayYearLong(progress.resubmissionDueDate * 1000)
        : '';
    const template = t('project_grade_explanation_failed', { score, maxScore: MAX_PROJECT_SCORE, dueDate });
    return parseI18nTemplate(template, {
        resubmitByDate: { visible: showResubmission },
        strong: { bold: true },
    });
}

// Check if we need a new page. If so, add one and return the new Y position.
// The pageAdded event handler (registered in downloadProjectFeedbackPdf) draws
// the template image, header, and footer on each new page.
function checkPageBreak(doc: PDFKit.PDFDocument, currentY: number, neededHeight: number): number {
    if (currentY + neededHeight > PAGE_BREAK_Y) {
        doc.addPage();
        return PAGE_N_CONTENT_START_Y;
    }
    return currentY;
}

// Returns the font for a segment. For inline code, returns the string 'Courier'
// (a PDFKit built-in); for bold/regular text, returns the registered ArrayBuffer font.
function resolveSegmentFont(segment: TextSegment, fonts: FetchedAssets['fonts']): ArrayBuffer | string {
    if (segment.code) return 'Courier';
    if (segment.bold) return fonts.semibold;
    return fonts.regular;
}

// Tokenize segments into styled words and whitespace for manual word-by-word layout.
// PDFKit's `continued: true` corrupts the text cursor when underline, link, or color
// changes happen mid-flow. By positioning each word ourselves we avoid this entirely.
type StyledToken = {
    text: string;
    font: ArrayBuffer | string;
    color: string;
    link?: string;
    code?: boolean;
    isSpace: boolean;
};

function tokenizeSegments(segments: TextSegment[], fonts: FetchedAssets['fonts'], defaultColor: string): StyledToken[] {
    const tokens: StyledToken[] = [];
    for (const segment of segments) {
        const font = resolveSegmentFont(segment, fonts);
        let tokenColor = defaultColor;
        if (segment.link) tokenColor = LINK_COLOR;
        else if (segment.code) tokenColor = COLOR_CODE;
        const parts = segment.text.split(/(\s+)/);
        for (const part of parts) {
            if (part !== '') {
                tokens.push({
                    text: part,
                    font,
                    color: tokenColor,
                    link: segment.link,
                    code: segment.code,
                    isSpace: /^\s+$/.test(part),
                });
            }
        }
    }
    return tokens;
}

// Draw a continuous link annotation + underline from startX to endX on one line.
function drawLinkSpan(
    doc: PDFKit.PDFDocument,
    startX: number,
    startY: number,
    endX: number,
    lineHeight: number,
    fontSize: number,
    url: string,
): void {
    doc.link(startX, startY, endX - startX, lineHeight, url);
    const underlineY = startY + fontSize;
    doc.save().moveTo(startX, underlineY).lineTo(endX, underlineY).strokeColor(LINK_COLOR).lineWidth(0.5).stroke();
    doc.restore();
}

// Draw a rounded background pill behind an inline code token.
function drawInlineCodeBackground(
    doc: PDFKit.PDFDocument,
    tokenX: number,
    tokenY: number,
    wordWidth: number,
    fontSize: number,
): void {
    doc.save()
        .roundedRect(
            tokenX - CODE_INLINE_PADDING_X,
            tokenY - CODE_INLINE_PADDING_Y,
            wordWidth + CODE_INLINE_PADDING_X * 2,
            fontSize + CODE_INLINE_PADDING_Y * 2,
            CODE_INLINE_RADIUS,
        )
        .fill(CODE_BG_COLOR);
    doc.restore();
}

// State tracked while laying out tokens word-by-word.
type ManualLayoutState = {
    curX: number;
    curY: number;
    linkStartX: number;
    linkStartY: number;
    linkUrl: string | null;
    codeSpanStartX: number;
    inCodeSpan: boolean;
    codeYOffset: number;
};

// Draw background for a code word. For continuation words in a multi-word span, also fill
// the space gap between the previous code word and this one with a plain rect so the
// background appears continuous without overlapping previously rendered text.
function drawCodeWordBackground(
    doc: PDFKit.PDFDocument,
    state: ManualLayoutState,
    textY: number,
    wordWidth: number,
    fontSize: number,
): void {
    if (state.codeSpanStartX < state.curX) {
        const gapWidth = state.curX - state.codeSpanStartX;
        const gapHeight = fontSize + CODE_INLINE_PADDING_Y * 2;
        doc.save()
            .rect(state.codeSpanStartX, textY - CODE_INLINE_PADDING_Y, gapWidth, gapHeight)
            .fill(CODE_BG_COLOR);
        doc.restore();
    }
    drawInlineCodeBackground(doc, state.curX, textY, wordWidth, fontSize);
    state.codeSpanStartX = state.curX + wordWidth;
}

// Lay out a single word token: wrap if needed, draw code background, render text, track links.
function layoutWordToken(
    doc: PDFKit.PDFDocument,
    token: StyledToken,
    state: ManualLayoutState,
    x: number,
    width: number,
    fontSize: number,
    lineHeight: number,
    bodyLineHeight: number,
): void {
    // When entering a code span, add spacing before and record span start
    if (token.code && !state.inCodeSpan) {
        if (state.curX > x) state.curX += CODE_INLINE_SPACING;
        state.inCodeSpan = true;
        state.codeSpanStartX = state.curX;
    }

    doc.font(token.font).fontSize(fontSize);
    const wordWidth = doc.widthOfString(token.text);

    if (state.curX > x && state.curX + wordWidth > x + width) {
        if (state.linkUrl) {
            drawLinkSpan(doc, state.linkStartX, state.linkStartY, state.curX, lineHeight, fontSize, state.linkUrl);
            state.linkUrl = null;
        }
        state.curX = x;
        state.curY += lineHeight;
        state.curY = checkPageBreak(doc, state.curY, lineHeight);
        // Reset code span start to the new line
        if (state.inCodeSpan) state.codeSpanStartX = state.curX;
    }

    // For inline code, shift Y down so the Courier baseline aligns with the body font baseline.
    const codeLineHeight = token.code ? doc.currentLineHeight(true) : 0;
    state.codeYOffset = token.code ? bodyLineHeight - codeLineHeight : 0;
    const textY = state.curY + state.codeYOffset;

    if (token.code) {
        drawCodeWordBackground(doc, state, textY, wordWidth, fontSize);
    }

    doc.font(token.font).fontSize(fontSize).fillColor(token.color);
    doc.text(token.text, state.curX, textY, { lineBreak: false });

    if (token.link && state.linkUrl !== token.link) {
        state.linkStartX = state.curX;
        state.linkStartY = state.curY;
        state.linkUrl = token.link;
    }

    state.curX += wordWidth;
}

// Render styled segments using manual word-by-word positioning.
// Each word is measured and placed explicitly, handling line wrapping ourselves
// so we never depend on PDFKit's buggy continued-text cursor.
function renderSegmentsManual(
    doc: PDFKit.PDFDocument,
    segments: TextSegment[],
    x: number,
    y: number,
    width: number,
    fonts: FetchedAssets['fonts'],
    fontSize: number,
    color: string,
): number {
    const tokens = tokenizeSegments(segments, fonts, color);
    if (tokens.length === 0) return y;

    doc.font(fonts.regular).fontSize(fontSize).lineGap(CONTENT_LINE_GAP);
    const lineHeight = doc.currentLineHeight(true);
    const state: ManualLayoutState = {
        curX: x,
        curY: y,
        linkStartX: 0,
        linkStartY: 0,
        linkUrl: null,
        codeSpanStartX: 0,
        inCodeSpan: false,
        codeYOffset: 0,
    };

    for (const token of tokens) {
        // Flush completed link spans
        if (token.link !== state.linkUrl && state.linkUrl) {
            drawLinkSpan(doc, state.linkStartX, state.linkStartY, state.curX, lineHeight, fontSize, state.linkUrl);
            state.linkUrl = null;
        }
        // When leaving a code span, add trailing spacing
        if (state.inCodeSpan && !token.code) {
            state.inCodeSpan = false;
            state.curX += CODE_INLINE_SPACING;
        }

        if (token.isSpace) {
            if (state.curX > x) {
                doc.font(token.font).fontSize(fontSize);
                state.curX += doc.widthOfString(token.text);
            }
        } else {
            layoutWordToken(doc, token, state, x, width, fontSize, lineHeight, lineHeight);
        }
    }

    // Add trailing spacing if paragraph ends with code
    if (state.inCodeSpan) state.curX += CODE_INLINE_SPACING;
    if (state.linkUrl) {
        drawLinkSpan(doc, state.linkStartX, state.linkStartY, state.curX, lineHeight, fontSize, state.linkUrl);
    }

    doc.x = x;
    doc.y = state.curY + lineHeight;
    return doc.y;
}

// Render a text segment array (with bold/italic/code/links) and return the ending Y position.
function renderSegments(
    doc: PDFKit.PDFDocument,
    segments: TextSegment[],
    x: number,
    y: number,
    width: number,
    fonts: FetchedAssets['fonts'],
    fontSize: number,
    color: string,
): number {
    if (segments.length === 0) return y;

    // Fast path: plain text with no inline styling — single doc.text() call
    if (!segments.some(s => s.bold || s.italic || s.link || s.code)) {
        doc.font(fonts.regular)
            .fontSize(fontSize)
            .fillColor(color)
            .lineGap(CONTENT_LINE_GAP)
            .text(segments.map(s => s.text).join(''), x, y, { width });
        return doc.y;
    }

    // Styled text: use manual word-by-word positioning to avoid PDFKit's
    // continued-text cursor bugs with underline/link/color changes.
    return renderSegmentsManual(doc, segments, x, y, width, fonts, fontSize, color);
}

// Render explanation segments that may include links and bold formatting
function renderExplanationSegments(
    doc: PDFKit.PDFDocument,
    segments: ExplanationSegment[],
    y: number,
    fonts: FetchedAssets['fonts'],
): void {
    doc.lineGap(CONTENT_LINE_GAP);
    segments.forEach((segment, i) => {
        const isLast = i === segments.length - 1;
        const font = segment.bold ? fonts.semibold : fonts.regular;
        const color = segment.link ? LINK_COLOR : COLOR_EXPLANATION;

        const options: Record<string, unknown> = { continued: !isLast };
        if (segment.link) {
            options.link = segment.link;
            options.underline = true;
        } else {
            options.underline = false;
        }

        if (i === 0) {
            doc.font(font)
                .fontSize(EXPLANATION_FONT_SIZE)
                .fillColor(color)
                .text(segment.text, CONTENT_LEFT, y, { width: CONTENT_WIDTH, ...options });
        } else {
            doc.font(font).fillColor(color).text(segment.text, options);
        }
    });
}

function renderHeadingBlock(doc: PDFKit.PDFDocument, text: string, y: number, assets: FetchedAssets): number {
    doc.font(assets.fonts.semibold).fontSize(HEADING_FONT_SIZE);
    const textHeight = doc.heightOfString(text, { width: CONTENT_WIDTH });
    let currentY = checkPageBreak(
        doc,
        y,
        SPACE_BEFORE_HEADING + textHeight + SPACE_AFTER_HEADING + MIN_CONTENT_AFTER_HEADING,
    );
    currentY += SPACE_BEFORE_HEADING;
    // Re-apply heading font after page break (checkPageBreak may trigger pageAdded which changes font)
    doc.font(assets.fonts.semibold)
        .fontSize(HEADING_FONT_SIZE)
        .fillColor(COLOR_BLACK)
        .text(text, CONTENT_LEFT, currentY, { width: CONTENT_WIDTH });
    return doc.y + SPACE_AFTER_HEADING;
}

function renderParagraphBlock(
    doc: PDFKit.PDFDocument,
    segments: TextSegment[],
    y: number,
    assets: FetchedAssets,
): number {
    // Only require space for a couple of lines — auto-pagination handles the rest
    const currentY = checkPageBreak(doc, y, MIN_PARAGRAPH_HEIGHT);
    return (
        renderSegments(
            doc,
            segments,
            CONTENT_LEFT,
            currentY,
            CONTENT_WIDTH,
            assets.fonts,
            BODY_FONT_SIZE,
            COLOR_BLACK,
        ) + PARAGRAPH_GAP
    );
}

function renderListBlock(
    doc: PDFKit.PDFDocument,
    items: ListItem[],
    startY: number,
    assets: FetchedAssets,
    ordered: boolean,
): number {
    let y = startY;
    items.forEach(item => {
        const indentOffset = item.indent * LIST_ITEM_INDENT;
        const itemX = CONTENT_LEFT + indentOffset;
        const itemWidth = CONTENT_WIDTH - indentOffset - LIST_ITEM_INDENT;
        const itemText = item.segments.map(s => s.text).join('');
        doc.font(assets.fonts.regular).fontSize(BODY_FONT_SIZE).lineGap(CONTENT_LINE_GAP);
        const itemHeight = doc.heightOfString(itemText, { width: itemWidth });
        y = checkPageBreak(doc, y, itemHeight + LIST_ITEM_GAP);

        // Render bullet or number marker
        const marker = ordered && item.index !== undefined ? `${item.index}.` : '\u2022';
        doc.font(assets.fonts.regular)
            .fontSize(BODY_FONT_SIZE)
            .fillColor(COLOR_BLACK)
            .text(marker, itemX, y, { width: LIST_ITEM_INDENT });

        const contentX = itemX + LIST_ITEM_INDENT;
        y =
            renderSegments(doc, item.segments, contentX, y, itemWidth, assets.fonts, BODY_FONT_SIZE, COLOR_BLACK) +
            LIST_ITEM_GAP;
    });
    return y + LIST_ITEM_GAP; // extra space after list
}

function renderCodeBlock(doc: PDFKit.PDFDocument, text: string, y: number, assets: FetchedAssets): number {
    const codeWidth = CONTENT_WIDTH - CODE_BLOCK_PADDING * 2;
    doc.font('Courier').fontSize(CODE_BLOCK_FONT_SIZE).lineGap(CODE_BLOCK_LINE_GAP);
    const textHeight = doc.heightOfString(text, { width: codeWidth });
    const blockHeight = textHeight + CODE_BLOCK_PADDING * 2;

    // Ensure the full block (plus top margin) fits on the current page
    const currentY = checkPageBreak(doc, y, CODE_BLOCK_MARGIN_TOP + blockHeight) + CODE_BLOCK_MARGIN_TOP;

    // Draw background rectangle then render code text inside the padded area.
    // Re-apply font after checkPageBreak since pageAdded handler may change it.
    doc.save()
        .roundedRect(CONTENT_LEFT, currentY, CONTENT_WIDTH, blockHeight, CODE_BLOCK_BORDER_RADIUS)
        .fill(CODE_BG_COLOR);
    doc.restore();
    doc.font('Courier')
        .fontSize(CODE_BLOCK_FONT_SIZE)
        .fillColor(COLOR_CODE)
        .lineGap(CODE_BLOCK_LINE_GAP)
        .text(text, CONTENT_LEFT + CODE_BLOCK_PADDING, currentY + CODE_BLOCK_PADDING, { width: codeWidth });

    // Restore font for subsequent content
    doc.font(assets.fonts.regular).fontSize(BODY_FONT_SIZE).lineGap(CONTENT_LINE_GAP);
    return doc.y + CODE_BLOCK_MARGIN_BOTTOM;
}

function renderHorizontalRule(doc: PDFKit.PDFDocument, y: number): number {
    const currentY = checkPageBreak(doc, y, HR_MARGIN_Y * 2 + 1) + HR_MARGIN_Y;
    doc.moveTo(CONTENT_LEFT, currentY)
        .lineTo(CONTENT_LEFT + CONTENT_WIDTH, currentY)
        .strokeColor(HR_COLOR)
        .lineWidth(TABLE_BORDER_WIDTH)
        .stroke();
    return currentY + HR_MARGIN_Y;
}

// Measure the height of a table row by finding the tallest cell
function measureTableRowHeight(
    doc: PDFKit.PDFDocument,
    row: TableRow,
    colWidths: number[],
    fonts: FetchedAssets['fonts'],
): number {
    let maxHeight = 0;
    row.cells.forEach((cell, colIdx) => {
        const cellWidth = (colWidths[colIdx] || colWidths[0]) - TABLE_CELL_PADDING_X * 2;
        const text = cell.map(s => s.text).join('');
        const hasBold = cell.some(s => s.bold);
        doc.font(hasBold ? fonts.semibold : fonts.regular).fontSize(BODY_FONT_SIZE);
        const h = doc.heightOfString(text, { width: cellWidth });
        if (h > maxHeight) maxHeight = h;
    });
    return maxHeight + TABLE_CELL_PADDING_Y * 2;
}

function renderTableRow(
    doc: PDFKit.PDFDocument,
    row: TableRow,
    y: number,
    colWidths: number[],
    rowHeight: number,
    alignments: ('left' | 'center' | 'right')[],
    assets: FetchedAssets,
    isHeader: boolean,
): void {
    let cellX = CONTENT_LEFT;
    // Draw row background for header
    if (isHeader) {
        doc.save().rect(CONTENT_LEFT, y, CONTENT_WIDTH, rowHeight).fill(TABLE_HEADER_BG);
        doc.restore();
    }
    // Draw cell borders and text
    row.cells.forEach((cell, colIdx) => {
        const colW = colWidths[colIdx] || colWidths[0];
        // Cell border
        doc.save()
            .rect(cellX, y, colW, rowHeight)
            .strokeColor(TABLE_BORDER_COLOR)
            .lineWidth(TABLE_BORDER_WIDTH)
            .stroke();
        doc.restore();
        // Cell text
        const textX = cellX + TABLE_CELL_PADDING_X;
        const textY = y + TABLE_CELL_PADDING_Y;
        const textWidth = colW - TABLE_CELL_PADDING_X * 2;
        const align = alignments[colIdx] || 'left';
        const font = isHeader ? assets.fonts.semibold : assets.fonts.regular;
        doc.font(font)
            .fontSize(BODY_FONT_SIZE)
            .fillColor(COLOR_BLACK)
            .text(cell.map(s => s.text).join(''), textX, textY, {
                width: textWidth,
                align,
            });
        cellX += colW;
    });
}

function renderTableBlock(
    doc: PDFKit.PDFDocument,
    block: Extract<ContentBlock, { type: 'table' }>,
    y: number,
    assets: FetchedAssets,
): number {
    const numCols = Math.max(block.headerRow.cells.length, 1);
    const colWidth = CONTENT_WIDTH / numCols;
    const colWidths = Array.from({ length: numCols }, () => colWidth);

    let currentY = y + TABLE_MARGIN_TOP;

    // Header row
    const headerHeight = measureTableRowHeight(doc, block.headerRow, colWidths, assets.fonts);
    currentY = checkPageBreak(doc, currentY, headerHeight);
    renderTableRow(doc, block.headerRow, currentY, colWidths, headerHeight, block.alignments, assets, true);
    currentY += headerHeight;

    // Body rows
    block.bodyRows.forEach(row => {
        const rowHeight = measureTableRowHeight(doc, row, colWidths, assets.fonts);
        currentY = checkPageBreak(doc, currentY, rowHeight);
        renderTableRow(doc, row, currentY, colWidths, rowHeight, block.alignments, assets, false);
        currentY += rowHeight;
    });

    return currentY + TABLE_MARGIN_BOTTOM;
}

function renderBlockquote(
    doc: PDFKit.PDFDocument,
    block: Extract<ContentBlock, { type: 'blockquote' }>,
    y: number,
    assets: FetchedAssets,
): number {
    const indentX = CONTENT_LEFT + BLOCKQUOTE_BORDER_WIDTH + BLOCKQUOTE_PADDING_LEFT;
    const indentWidth = CONTENT_WIDTH - BLOCKQUOTE_BORDER_WIDTH - BLOCKQUOTE_PADDING_LEFT;
    const startY = y + BLOCKQUOTE_MARGIN_Y;

    // Render inner blocks in the indented area
    let innerY = startY;
    block.blocks.forEach(inner => {
        if (inner.type === 'paragraph') {
            const currentY = checkPageBreak(doc, innerY, MIN_PARAGRAPH_HEIGHT);
            innerY =
                renderSegments(
                    doc,
                    inner.segments,
                    indentX,
                    currentY,
                    indentWidth,
                    assets.fonts,
                    BODY_FONT_SIZE,
                    COLOR_BLACK,
                ) + PARAGRAPH_GAP;
        } else {
            // For non-paragraph content inside blockquotes, render as-is at the indented position
            innerY = renderContentBlocks(doc, [inner], innerY, assets);
        }
    });

    // Draw the left border line alongside the content
    doc.save()
        .moveTo(CONTENT_LEFT + BLOCKQUOTE_BORDER_WIDTH / 2, startY)
        .lineTo(CONTENT_LEFT + BLOCKQUOTE_BORDER_WIDTH / 2, innerY - PARAGRAPH_GAP)
        .strokeColor(BLOCKQUOTE_BORDER_COLOR)
        .lineWidth(BLOCKQUOTE_BORDER_WIDTH)
        .stroke();
    doc.restore();

    return innerY + BLOCKQUOTE_MARGIN_Y;
}

function renderContentBlocks(
    doc: PDFKit.PDFDocument,
    blocks: ContentBlock[],
    startY: number,
    assets: FetchedAssets,
): number {
    let y = startY;

    blocks.forEach(block => {
        switch (block.type) {
            case 'heading':
                y = renderHeadingBlock(doc, block.text, y, assets);
                break;
            case 'paragraph':
                y = renderParagraphBlock(doc, block.segments, y, assets);
                break;
            case 'list':
                y = renderListBlock(doc, block.items, y, assets, block.ordered);
                break;
            case 'codeBlock':
                y = renderCodeBlock(doc, block.text, y, assets);
                break;
            case 'table':
                y = renderTableBlock(doc, block, y, assets);
                break;
            case 'horizontalRule':
                y = renderHorizontalRule(doc, y);
                break;
            case 'blockquote':
                y = renderBlockquote(doc, block, y, assets);
                break;
            default:
                break;
        }
    });

    return y;
}

function renderPage1Metadata(
    doc: PDFKit.PDFDocument,
    params: DownloadProjectFeedbackPdfParams,
    fonts: FetchedAssets['fonts'],
    startY: number,
): number {
    const { progress, failed, groupMemberNames, submitterName, t } = params;
    let y = startY;

    if (groupMemberNames && groupMemberNames.length > 0) {
        doc.font(fonts.semibold)
            .fontSize(METADATA_FONT_SIZE)
            .fillColor(COLOR_BLACK)
            .text(`${t('project_team')} `, CONTENT_LEFT, y, { continued: true });
        doc.font(fonts.regular).text(groupMemberNames.join(', '));
        y = doc.y + 3;
    } else if (submitterName) {
        doc.font(fonts.semibold)
            .fontSize(METADATA_FONT_SIZE)
            .fillColor(COLOR_BLACK)
            .text(`${t('project_submitter')} `, CONTENT_LEFT, y, { continued: true });
        doc.font(fonts.regular).text(submitterName);
        y = doc.y + 3;
    }

    if (progress.projectSubmittedAt) {
        const submissionDate = formattedUserFacingMonthDayYearLong(progress.projectSubmittedAt * 1000, false);
        doc.font(fonts.semibold)
            .fontSize(METADATA_FONT_SIZE)
            .fillColor(COLOR_BLACK)
            .text(`${t('submission_date')} `, CONTENT_LEFT, y, { continued: true });
        doc.font(fonts.regular).text(submissionDate);
        y = doc.y + 3;
    }

    const isResubmissionRequired = progress.resubmissionRequired;
    const gradeColor = failed || isResubmissionRequired ? COLOR_RED : COLOR_BLACK;
    const gradeText = isResubmissionRequired
        ? t('resubmission_required_label')
        : `${progress.score} / ${MAX_PROJECT_SCORE}`;
    doc.font(fonts.semibold)
        .fontSize(METADATA_FONT_SIZE)
        .fillColor(COLOR_BLACK)
        .text(`${t('project_grade')} `, CONTENT_LEFT, y, { continued: true });
    doc.font(fonts.regular).fillColor(gradeColor).text(gradeText);
    return doc.y + 12;
}

function renderPage1Content(
    doc: PDFKit.PDFDocument,
    params: DownloadProjectFeedbackPdfParams,
    assets: FetchedAssets,
): number {
    const { project, progress, passed, t } = params;
    const score = progress.score ?? 0;

    doc.image(assets.pages.page1, 0, 0, { width: PAGE_WIDTH, height: PAGE_HEIGHT });

    // Project title (large)
    doc.font(assets.fonts.regular)
        .fontSize(TITLE_FONT_SIZE)
        .fillColor(COLOR_BLACK)
        .text(project.title, CONTENT_LEFT, PAGE_1_TITLE_Y, { width: CONTENT_WIDTH });
    let y = doc.y + PAGE_1_SUBTITLE_Y_OFFSET;

    // Subtitle
    doc.font(assets.fonts.regular)
        .fontSize(SUBTITLE_FONT_SIZE)
        .fillColor(COLOR_MUTED)
        .text(t('title'), CONTENT_LEFT, y, { width: CONTENT_WIDTH });
    y = doc.y + PAGE_1_METADATA_Y_OFFSET;

    // Metadata (team, date, grade)
    y = renderPage1Metadata(doc, params, assets.fonts, y);

    // Pass/fail explanation
    doc.font(assets.fonts.regular)
        .fontSize(EXPLANATION_FONT_SIZE)
        .fillColor(COLOR_EXPLANATION)
        .lineGap(CONTENT_LINE_GAP);

    if (progress.resubmissionRequired) {
        const segments = getResubmissionRequiredExplanationSegments(t, progress);
        renderExplanationSegments(doc, segments, y, assets.fonts);
    } else if (passed) {
        const segments = getPassedExplanationSegments(t, score);
        renderExplanationSegments(doc, segments, y, assets.fonts);
    } else {
        const segments = getFailedExplanationSegments(t, score, progress);
        renderExplanationSegments(doc, segments, y, assets.fonts);
    }
    doc.lineGap(0);
    y = doc.y + 15;

    // Divider line
    y = checkPageBreak(doc, y, 20);
    doc.moveTo(CONTENT_LEFT, y)
        .lineTo(CONTENT_LEFT + CONTENT_WIDTH, y)
        .strokeColor(COLOR_BLACK)
        .lineWidth(0.5)
        .stroke();
    y += 15;

    addCopyrightFooter(doc, assets.fonts);
    return y;
}

function handlePageAdded(doc: PDFKit.PDFDocument, assets: FetchedAssets, projectTitle: string) {
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    const pdfDoc = doc as any;
    const savedFont = pdfDoc._font;
    const savedFontSize = pdfDoc._fontSize;
    const savedFillColor = pdfDoc._fillColor;
    const savedLineGap = pdfDoc._lineGap;
    const savedX = doc.x;
    const savedY = doc.y;

    doc.image(assets.pages.page2, 0, 0, { width: PAGE_WIDTH, height: PAGE_HEIGHT });
    doc.font(assets.fonts.regular)
        .fontSize(RUNNING_HEADER_FONT_SIZE)
        .fillColor(COLOR_MUTED)
        .text(projectTitle, CONTENT_LEFT, PAGE_N_HEADER_Y);
    addCopyrightFooter(doc, assets.fonts);

    // Restore all state so the text that triggered pagination continues correctly.
    // We only assign private properties here (no PDFKit method calls) because this
    // handler fires mid-render — calling doc.fillColor() / doc.lineGap() can put
    // the document into a bad state and freeze the browser.
    pdfDoc._font = savedFont;
    pdfDoc._fontSize = savedFontSize;
    pdfDoc._fillColor = savedFillColor;
    pdfDoc._lineGap = savedLineGap;
    doc.x = savedX;
    doc.y = savedY;
}

async function saveOrSharePdf(blob: Blob, fileName: string) {
    if (downloadSupported()) {
        saveAs(blob, fileName);
    } else if (await shareSupported()) {
        const dataUrl = await blobToDataURL(blob);
        (window as FrontRoyalWindow).plugins.socialsharing!.share(null, null, `df:${fileName};${dataUrl}`, null);
    }
}

export async function downloadProjectFeedbackPdf(params: DownloadProjectFeedbackPdfParams): Promise<void> {
    const { project, progress, brand, t } = params;

    if (progress.score == null) {
        throw new Error('Cannot generate PDF: project score is not available');
    }

    await loadPdfDependencies();
    const assets = await fetchAssets(brand);

    const globalWindow = window as FrontRoyalWindow;
    const doc = new globalWindow.PDFDocument({
        size: [PAGE_WIDTH, PAGE_HEIGHT],
        margins: {
            top: PAGE_N_CONTENT_START_Y,
            bottom: PAGE_HEIGHT - PAGE_BREAK_Y,
            left: CONTENT_LEFT,
            right: PAGE_WIDTH - CONTENT_LEFT - CONTENT_WIDTH,
        },
    });
    const pdfStream = doc.pipe(globalWindow.blobStream());

    // When PDFKit auto-paginates (text overflows past PAGE_BREAK_Y), this handler
    // draws the content page template, running header, and copyright footer.
    // We save/restore PDFKit's internal rendering state so ongoing text rendering isn't affected.
    doc.on('pageAdded', () => handlePageAdded(doc, assets, project.title));

    const y = renderPage1Content(doc, params, assets);

    // Render feedback markdown
    const blocks = parseMarkdownForPdf(progress.feedback || '');
    renderContentBlocks(doc, blocks, y, assets);

    // Finish and download/share
    pdfStream.on('finish', async () => {
        try {
            const blob = pdfStream.toBlob('application/pdf');
            const sanitizedTitle = project.title.replace(/[^a-zA-Z0-9\s-]/g, '').trim();
            await saveOrSharePdf(blob, `${sanitizedTitle} - ${t('title')}.pdf`);
        } catch (e) {
            const ErrorLogService = angularInjectorProvider.safeGet<typeof ErrorLogServiceClass>('ErrorLogService');
            if (ErrorLogService) {
                ErrorLogService.notify('Failed to save or share project feedback PDF', e);
            }
        }
    });

    doc.end();
}
